import { useRef, useState } from "react";
import { IconBraces, IconDecimal, IconGripVertical, IconTrash } from "@tabler/icons-react";
import { Badge, Card, IconButton, Select, Text } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { RepDefinicion } from "../services/reporteria.api";
import * as ops from "../state/builder-ops";
import { FormatoDialog, TransformarDialog } from "./ColumnaDialogs";

const AGREGACIONES = [
  { value: "", label: "Dimensión (agrupa)" },
  { value: "sum", label: "Suma" },
  { value: "avg", label: "Promedio" },
  { value: "count", label: "Cuenta" },
  { value: "countd", label: "Cuenta distintos" },
  { value: "min", label: "Mínimo" },
  { value: "max", label: "Máximo" },
];

export function ColumnasSeleccionadas({
  def,
  setDef,
  fetchValores,
  contexto,
  avanzado = true,
}: {
  def: RepDefinicion;
  setDef: (f: (d: RepDefinicion) => RepDefinicion) => void;
  /** Cómo pedir los valores distintos de una columna (para el diálogo Transformar). */
  fetchValores?: (token: string) => Promise<unknown[]>;
  contexto?: string;
  /** Modo avanzado · en simple se ocultan la agregación y la transformación (NCalc). */
  avanzado?: boolean;
}) {
  const [dialog, setDialog] = useState<{ tipo: "formato" | "transformar"; index: number } | null>(null);
  // Índice origen del arrastre en un ref: disponible sincronamente en el drop
  // (el estado no se re-renderiza entre dragstart y drop en el mismo tick).
  const origen = useRef<number | null>(null);
  const [arrastrando, setArrastrando] = useState<number | null>(null);
  const [sobre, setSobre] = useState<number | null>(null);

  function soltar(destino: number) {
    const desde = origen.current;
    if (desde != null && desde !== destino) setDef((d) => ops.reordenarColumna(d, desde, destino));
    origen.current = null;
    setArrastrando(null);
    setSobre(null);
  }

  if (def.columnas.length === 0) {
    return (
      <Card className="p-4">
        <Text size="sm" c="dimmed">
          Elige columnas del panel de datos para construir el reporte.
        </Text>
      </Card>
    );
  }

  return (
    <Card className="p-3">
      <div className="mb-2 flex items-center justify-between">
        <Text fw={600}>Columnas del reporte</Text>
        <Badge variant="light" size="xs">
          {def.columnas.length}
        </Badge>
      </div>
      <div className="space-y-1">
        {def.columnas.map((c, i) => {
          const transformada = !!c.expresion;
          return (
            <div
              key={`${c.tablaAlias}-${c.token}`}
              onDragOver={(e) => {
                if (arrastrando != null) {
                  e.preventDefault();
                  setSobre(i);
                }
              }}
              onDrop={() => soltar(i)}
              className={cn(
                "flex items-center gap-2 rounded-md border border-border p-1.5",
                arrastrando === i && "opacity-50",
                sobre === i && arrastrando !== i && "border-multiva-400 bg-multiva-50 dark:bg-multiva-900/20"
              )}
            >
              <span
                draggable
                onDragStart={(e) => {
                  origen.current = i;
                  setArrastrando(i);
                  e.dataTransfer.effectAllowed = "move";
                }}
                onDragEnd={() => {
                  origen.current = null;
                  setArrastrando(null);
                  setSobre(null);
                }}
                title="Arrastra para reordenar"
                className="cursor-grab text-muted-foreground active:cursor-grabbing"
              >
                <IconGripVertical size={16} />
              </span>

              <input
                className="h-8 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-sm"
                value={c.etiqueta ?? ""}
                placeholder="Etiqueta"
                onChange={(e) => {
                  // Capturamos el valor ANTES del updater · React anula
                  // `e.currentTarget` cuando el updater corre después (null.value).
                  const etiqueta = e.currentTarget.value;
                  setDef((d) => ops.updateColumna(d, i, { etiqueta }));
                }}
              />

              {def.agrupar && avanzado && (
                <div className="w-44 shrink-0">
                  <Select
                    size="sm"
                    value={c.agregacion ?? ""}
                    onValueChange={(v) => setDef((d) => ops.updateColumna(d, i, { agregacion: v || null }))}
                    options={AGREGACIONES}
                  />
                </div>
              )}

              <IconButton
                aria-label="Formato"
                title={transformada ? "Formato/transformación aplicado" : "Formato"}
                variant={transformada ? "light" : "subtle"}
                size="sm"
                onClick={() => setDialog({ tipo: "formato", index: i })}
              >
                <IconDecimal size={16} />
              </IconButton>
              {avanzado && (
                <IconButton
                  aria-label="Transformar"
                  title="Transformar valores"
                  variant={c.mapaValores ? "light" : "subtle"}
                  size="sm"
                  onClick={() => setDialog({ tipo: "transformar", index: i })}
                >
                  <IconBraces size={16} />
                </IconButton>
              )}
              <IconButton aria-label="Quitar" variant="subtle" size="sm" onClick={() => setDef((d) => ops.removeColumna(d, i))}>
                <IconTrash size={16} />
              </IconButton>
            </div>
          );
        })}
      </div>

      {dialog?.tipo === "formato" && (
        <FormatoDialog
          columna={def.columnas[dialog.index]}
          onAplicar={(patch) => setDef((d) => ops.updateColumna(d, dialog.index, patch))}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.tipo === "transformar" && (
        <TransformarDialog
          columna={def.columnas[dialog.index]}
          onAplicar={(patch) => setDef((d) => ops.updateColumna(d, dialog.index, patch))}
          onClose={() => setDialog(null)}
          fetchValores={fetchValores}
          contexto={contexto}
        />
      )}
    </Card>
  );
}
